import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Check, ChefHat, Sparkles, Zap, Crown } from "lucide-react";
import { createPageUrl } from "@/utils";

const PLANS = [
  {
    code: "basic",
    name: "Bàsic",
    icon: ChefHat,
    color: "border-gray-200",
    headerColor: "bg-gray-50",
    price_monthly: 29,
    price_yearly: 290,
    description: "Per a restaurants que volen digitalitzar l'operativa bàsica.",
    features: [
      "Control horari",
      "Registre de temperatures",
      "Control de clor",
      "Neteja i desinfecció",
      "Gestió d'incidències",
      "Perfil del local",
      "Equip (fins 5 membres)",
    ],
  },
  {
    code: "professional",
    name: "Professional",
    icon: Zap,
    color: "border-[#7B2D3F] ring-2 ring-[#7B2D3F]/20",
    headerColor: "bg-[#7B2D3F]",
    price_monthly: 59,
    price_yearly: 590,
    popular: true,
    description: "El pla més complet per a restaurants amb necessitats avançades.",
    features: [
      "Tot el Bàsic, més:",
      "Documents i descàrregues",
      "Notificacions i alertes",
      "Verificacions APPCC",
      "Fitxes tècniques de receptes",
      "Gestió d'ingredients",
      "Dashboard de compliment",
      "APPCC complet",
    ],
  },
  {
    code: "premium",
    name: "Premium",
    icon: Crown,
    color: "border-amber-300",
    headerColor: "bg-gradient-to-br from-amber-500 to-amber-600",
    price_monthly: 99,
    price_yearly: 990,
    description: "Per a grups o locals que necessiten compliment normatiu total.",
    features: [
      "Tot el Professional, més:",
      "RGPD complet",
      "PRL (prevenció riscos laborals)",
      "Protocol d'assetjament",
      "Analítica avançada",
      "Generador de documents IA",
      "Normativa regulatòria",
      "Equip il·limitat",
    ],
  },
];

export default function SelectPlan() {
  const urlParams = new URLSearchParams(window.location.search);
  const venueId = urlParams.get("venue_id");
  const isNew = urlParams.get("new") === "1";

  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState("");

  const handleSelectPlan = async (plan) => {
    if (!venueId) { setError("No s'ha trobat el local."); return; }
    setLoading(plan.code);
    setError("");

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_URL}/api/checkout/session`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plan: plan.code,
          billing_cycle: billing,
          venue_id: venueId,
          origin_url: window.location.origin,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Error creant la sessió de pagament.');
      }

      const data = await response.json();
      if (data?.url) {
        window.location.href = data.url;
      } else {
        setError("Error creant la sessió de pagament.");
      }
    } catch (err) {
      setError("Error: " + err.message);
    } finally {
      setLoading(null);
    }
  };

  const handleTrial = async () => {
    // Already created trial on register, just redirect to dashboard
    window.location.href = createPageUrl("Dashboard");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8f3f4] to-[#f0e8ea] py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-[#7B2D3F]/10 text-[#7B2D3F] text-sm font-semibold px-4 py-1.5 rounded-full mb-4">
            <Sparkles className="w-4 h-4" />
            {isNew ? "Benvingut a Kitcha!" : "Canvia de pla"}
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Escull el teu pla</h1>
          <p className="text-gray-500 max-w-lg mx-auto">
            {isNew
              ? "Tens 14 dies de prova gratuïta. Pots triar un pla ara o explorar l'app primer."
              : "Canvia o actualitza la teva subscripció en qualsevol moment."}
          </p>

          {/* Billing toggle */}
          <div className="inline-flex items-center gap-3 bg-white rounded-xl p-1.5 shadow-sm border border-gray-100 mt-6">
            <button
              onClick={() => setBilling("monthly")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${billing === "monthly" ? "bg-[#7B2D3F] text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              Mensual
            </button>
            <button
              onClick={() => setBilling("yearly")}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${billing === "yearly" ? "bg-[#7B2D3F] text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
            >
              Anual
              <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${billing === "yearly" ? "bg-white/20 text-white" : "bg-green-100 text-green-700"}`}>
                -17%
              </span>
            </button>
          </div>
        </div>

        {/* Plans grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            const price = billing === "yearly" ? plan.price_yearly / 12 : plan.price_monthly;
            const isLoading = loading === plan.code;

            return (
              <div
                key={plan.code}
                className={`bg-white rounded-2xl border-2 ${plan.color} overflow-hidden shadow-sm hover:shadow-md transition-all relative`}
              >
                {plan.popular && (
                  <div className="absolute top-4 right-4 bg-[#7B2D3F] text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    Més popular
                  </div>
                )}

                {/* Header */}
                <div className={`${plan.headerColor} p-6 ${plan.code === "professional" ? "text-white" : ""}`}>
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${plan.code === "professional" ? "bg-white/20" : plan.code === "premium" ? "bg-white/20" : "bg-gray-200"}`}>
                    <Icon className={`w-5 h-5 ${plan.code === "professional" || plan.code === "premium" ? "text-white" : "text-gray-600"}`} />
                  </div>
                  <h3 className={`text-xl font-bold mb-1 ${plan.code === "professional" || plan.code === "premium" ? "text-white" : "text-gray-900"}`}>
                    {plan.name}
                  </h3>
                  <p className={`text-sm ${plan.code === "professional" ? "text-white/80" : plan.code === "premium" ? "text-white/80" : "text-gray-500"}`}>
                    {plan.description}
                  </p>
                </div>

                {/* Price */}
                <div className="px-6 py-5 border-b border-gray-100">
                  <div className="flex items-end gap-1">
                    <span className="text-4xl font-extrabold text-gray-900">{Math.round(price)}€</span>
                    <span className="text-gray-400 mb-1.5">/mes</span>
                  </div>
                  {billing === "yearly" && (
                    <p className="text-xs text-green-600 font-medium mt-1">
                      Facturat {plan.price_yearly}€/any · Estalvies {plan.price_monthly * 12 - plan.price_yearly}€
                    </p>
                  )}
                </div>

                {/* Features */}
                <div className="px-6 py-5">
                  <ul className="space-y-3">
                    {plan.features.map((f, i) => (
                      <li key={`feat-${i}`} className="flex items-start gap-2.5 text-sm">
                        <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${i === 0 && plan.code !== "basic" ? "text-gray-400" : "text-green-500"}`} />
                        <span className={i === 0 && plan.code !== "basic" ? "text-gray-400 italic" : "text-gray-600"}>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA */}
                <div className="px-6 pb-6">
                  <Button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={isLoading}
                    className={`w-full h-11 font-semibold rounded-xl ${
                      plan.code === "professional"
                        ? "bg-[#7B2D3F] hover:bg-[#5B1D2F] text-white"
                        : plan.code === "premium"
                        ? "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white"
                        : "bg-gray-900 hover:bg-gray-800 text-white"
                    }`}
                  >
                    {isLoading ? "Carregant..." : `Escollir ${plan.name}`}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 text-center">
            {error}
          </div>
        )}

        {isNew && (
          <div className="text-center mt-8">
            <button
              onClick={handleTrial}
              className="text-gray-500 text-sm underline underline-offset-2 hover:text-gray-700"
            >
              Continuar amb la prova gratuïta de 14 dies →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}